import { Link } from "react-router-dom";
import { Calendar, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getImageUrl } from "@/utils/imageUrl";

export function NewsCard({ post, variant = "default", className = "" }) {
  const {
    slug,
    title,
    excerpt,
    featured_image_url,
    published_at,
    view_count,
    post_type,
    location_scope,
  } = post;

  // Get correct image URL
  const imageUrl = getImageUrl(featured_image_url);

  const typeLabels = {
    news: "Noticia",
    destination: "Destino",
    experience: "Experiencia",
    flavor: "Sabor",
    video: "Video",
  };

  const typeColors = {
    news: "bg-blue-100 text-blue-800",
    destination: "bg-green-100 text-green-800",
    experience: "bg-purple-100 text-purple-800",
    flavor: "bg-orange-100 text-orange-800",
    video: "bg-red-100 text-red-800",
  };

  const getUrl = () => {
    const routes = {
      news: "/noticias",
      destination: "/destinos",
      experience: "/experiencias",
      flavor: "/sabores",
      video: "/videos",
    };
    return `${routes[post_type] || "/noticias"}/${slug}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-HN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const placeholderImage = "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80";

  if (variant === "featured") {
    return (
      <Link
        to={getUrl()}
        className={`group relative overflow-hidden rounded-3xl bg-white shadow-card hover:shadow-card-hover transition-all duration-500 block ${className}`}
        data-testid={`news-card-${slug}`}
      >
        <div className="absolute inset-0">
          <img
            src={imageUrl || placeholderImage}
            alt={title}
            className="w-full h-full object-cover img-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        </div>
        <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
          <div className="flex items-center gap-2 mb-3">
            <Badge className={`${typeColors[post_type]} border-0 text-xs font-bold uppercase tracking-wider`}>
              {typeLabels[post_type]}
            </Badge>
            {location_scope && (
              <Badge variant="secondary" className="bg-white/20 text-white border-0">
                {location_scope === "choluteca" ? "Choluteca" : location_scope === "honduras" ? "Honduras" : "Ambos"}
              </Badge>
            )}
          </div>
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3 line-clamp-2 group-hover:text-accent transition-colors">
            {title}
          </h3>
          {excerpt && (
            <p className="text-gray-200 text-sm line-clamp-2 mb-4">{excerpt}</p>
          )}
          <div className="flex items-center gap-4 text-sm text-gray-300">
            {published_at && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(published_at)}
              </span>
            )}
            {view_count > 0 && (
              <span className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                {view_count}
              </span>
            )}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={getUrl()}
      className={`group relative overflow-hidden rounded-3xl bg-white shadow-card hover:shadow-card-hover transition-all duration-500 block h-full ${className}`}
      data-testid={`news-card-${slug}`}
    >
      <div className="absolute inset-0">
        <img
          src={imageUrl || placeholderImage}
          alt={title}
          className="w-full h-full object-cover img-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      </div>
      <div className="relative h-full flex flex-col justify-end p-5">
        <Badge className={`${typeColors[post_type]} border-0 text-xs font-bold uppercase tracking-wider w-fit mb-2`}>
          {typeLabels[post_type]}
        </Badge>
        <h3 className="font-heading text-lg font-bold text-white line-clamp-2 group-hover:text-accent transition-colors">
          {title}
        </h3>
        {published_at && (
          <p className="text-gray-300 text-xs mt-2 flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {formatDate(published_at)}
          </p>
        )}
      </div>
    </Link>
  );
}

export function NewsCardSkeleton({ variant = "default" }) {
  return (
    <div className={`rounded-3xl bg-gray-100 animate-pulse ${variant === "featured" ? "h-full min-h-[400px]" : "h-[300px]"}`}>
      <div className="h-full flex flex-col justify-end p-5">
        <div className="h-4 w-16 bg-gray-200 rounded-full mb-3" />
        <div className="h-6 w-full bg-gray-200 rounded mb-2" />
        <div className="h-6 w-3/4 bg-gray-200 rounded" />
      </div>
    </div>
  );
}
